import React from "react";
import { Link } from "react-router-dom";
import { BookOpen, MapPin, Sparkles, Timer, ArrowRight } from "lucide-react";
import { InstallButton } from "../components/InstallButton.jsx";
import Seo from "../components/Seo.jsx";

export default function Landing() {
  return (
    <div className="min-h-screen bg-brimi-cream">
      <Seo
        title="BrimiH&G — AI Study App for ICSE 2028 (Class IX-X)"
        description="Ace ICSE 2028 with BrimiH&G — AI chapter notes, MCQ practice, a Claude-powered tutor chat, 20-question timed mock tests, and an India + World tap-to-place map quiz for Class IX and X. Start your 20-day free trial."
        path="/"
      />
      <header className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
        <Link to="/" className="font-heading font-bold text-xl text-brimi-navy" data-testid="brand-link">
          Brimi<span className="text-brimi-terracotta">H</span>&<span className="text-brimi-sage">G</span>
        </Link>
        <div className="flex items-center gap-2">
          <InstallButton variant="header" />
          <Link to="/login" className="brimi-btn-ghost" data-testid="header-login-link">Login</Link>
          <Link to="/signup" className="brimi-btn-primary" data-testid="header-signup-btn">
            Start free <ArrowRight size={16} />
          </Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-5 pt-10 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="stagger-in">
          <span className="brimi-pill bg-brimi-terracotta/10 text-brimi-terracotta">ICSE 2028 · Class IX–X</span>
          <h1 className="font-heading font-bold tracking-tight text-brimi-navyText text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05]">
            Your AI study companion for{" "}
            <span className="text-brimi-terracotta">History & Civics</span> and{" "}
            <span className="text-brimi-sage">Geography</span>.
          </h1>
          <p className="text-lg text-brimi-navyText/75 mt-5 leading-relaxed max-w-xl">
            AI-generated chapter notes, MCQ practice, timed mock tests, and a tap-to-place India map quiz — built strictly to
            the ICSE 2028 syllabus.
          </p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Link to="/signup" className="brimi-btn-primary text-base" data-testid="hero-signup-btn">
              Start 20-day free trial <ArrowRight size={18} />
            </Link>
            <Link to="/login" className="brimi-btn-ghost text-base" data-testid="hero-login-link">
              I already have an account
            </Link>
          </div>
          <p className="text-xs text-brimi-navyText/55 mt-3">No card required · ₹299/mo or ₹2499/yr after</p>
        </div>

        <div className="relative" style={{ animation: "stagger-in 500ms ease 80ms both" }}>
          <div className="absolute -inset-4 bg-brimi-terracotta/10 rounded-3xl rotate-2" aria-hidden />
          <div className="absolute -inset-4 bg-brimi-sage/10 rounded-3xl -rotate-2" aria-hidden />
          <img
            src="https://images.unsplash.com/photo-1760420957123-08a562e52940?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBzdHVkZW50JTIwc3R1ZHlpbmclMjBkZXNrfGVufDB8fHx8MTc4MjM2MzA0Mnww&ixlib=rb-4.1.0&q=85"
            alt="Student studying"
            className="relative rounded-3xl shadow-md w-full object-cover aspect-[4/5]"
          />
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 pb-20">
        <h2 className="text-2xl sm:text-3xl font-heading font-semibold text-brimi-navyText mb-8">Built for the way you actually study</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { i: BookOpen, title: "AI chapter notes", desc: "Crisp, exam-focused notes for every ICSE chapter. Download as PDF.", color: "terracotta" },
            { i: Sparkles, title: "Smart MCQs", desc: "Fresh questions generated chapter-by-chapter. Instant scoring.", color: "sage" },
            { i: Timer, title: "20-Q mock tests", desc: "Timed, mixed-chapter mocks with detailed breakdown.", color: "terracotta" },
            { i: MapPin, title: "India map quiz", desc: "Tap-to-place rivers, mountains, and cities. Real geography.", color: "sage" },
          ].map((f, i) => (
            <div key={i} className="brimi-card p-6">
              <div
                className={`w-11 h-11 rounded-xl grid place-items-center mb-4 ${
                  f.color === "terracotta" ? "bg-brimi-terracotta/15 text-brimi-terracotta" : "bg-brimi-sage/15 text-brimi-sage"
                }`}
              >
                <f.i size={20} />
              </div>
              <h3 className="font-heading font-semibold text-lg text-brimi-navyText mb-1">{f.title}</h3>
              <p className="text-sm text-brimi-navyText/70 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 pb-16">
        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          <div className="lg:col-span-2">
            <h2 className="text-2xl sm:text-3xl font-heading font-semibold text-brimi-navyText">
              Built specifically for the ICSE 2028 syllabus
            </h2>
            <p className="text-brimi-navyText/80 leading-relaxed mt-4">
              BrimiH&amp;G covers every chapter of <strong>History &amp; Civics</strong> and{" "}
              <strong>Geography</strong> for ICSE Class IX and Class X — from the Harappan Civilisation,
              Vedic Period, Mauryan Empire, Mughals and the Indian National Movement, all the way to the
              First and Second World Wars, the United Nations and the Non-Aligned Movement. The Geography
              syllabus is fully covered too: structure of the earth, hydrosphere, atmosphere, natural
              regions of the world, climate of India, soils, vegetation, minerals, agriculture, industries,
              transport and waste management.
            </p>
            <p className="text-brimi-navyText/80 leading-relaxed mt-3">
              Every chapter has crisp AI-generated notes that you can read aloud or download as a PDF,
              ten fresh MCQs per chapter, and a personal AI tutor (powered by Claude Sonnet 4.5) that
              answers your questions and stays strictly within the ICSE 2028 scope. When you&apos;re ready
              for revision, the 20-question timed mock test picks chapters at random and gives you a
              chapter-wise breakdown of your performance.
            </p>
            <p className="text-brimi-navyText/80 leading-relaxed mt-3">
              Map work — often the trickiest part of the Geography paper — gets its own interactive
              tap-to-place quiz. Practise both the World map (rivers, mountains, oceans, gulfs, straits,
              natural regions) and the India map (mountains, peaks, plains, rivers, water bodies, passes,
              latitudes) on real GeoJSON outlines, with instant feedback within 250&nbsp;km tolerance.
            </p>
          </div>
          <div className="lg:col-span-1 space-y-5">
            <div className="brimi-card p-5">
              <p className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/60">Pricing</p>
              <p className="font-heading font-bold text-brimi-navyText text-2xl mt-2">20-day free trial</p>
              <p className="text-sm text-brimi-navyText/70 mt-1">No card required. Cancel anytime.</p>
              <ul className="mt-3 space-y-1.5 text-sm text-brimi-navyText/85">
                <li>• Monthly &mdash; <strong>₹299/mo</strong></li>
                <li>• Yearly &mdash; <strong>₹2,499/yr</strong> (save ₹1,089)</li>
              </ul>
            </div>
            <div className="brimi-card p-5">
              <p className="text-xs uppercase tracking-widest font-semibold text-brimi-navyText/60">For students</p>
              <p className="text-sm text-brimi-navyText/80 mt-2 leading-relaxed">
                BrimiH&amp;G installs as an app on your phone or laptop. Works offline-light, supports
                voice questions, and reads notes aloud so you can revise while walking, eating, or just
                resting your eyes.
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-5 py-8 text-sm text-brimi-navyText/60 border-t border-brimi-creamSoft flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <span>© {new Date().getFullYear()} BrimiH&G · ICSE 2028 study app</span>
        <div className="flex items-center gap-4">
          <Link to="/privacy" className="hover:text-brimi-navy" data-testid="footer-privacy-link">Privacy</Link>
          <Link to="/terms" className="hover:text-brimi-navy" data-testid="footer-terms-link">Terms</Link>
          <a href="mailto:brajeshc56@gmail.com" className="hover:text-brimi-navy">Contact</a>
        </div>
      </footer>
    </div>
  );
}
